import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { GpsModeCellRendererComponent } from './cell-renderers/gps-mode-cell-renderer/gps-mode-cell-renderer.component';
import { MatDialog } from '@angular/material/dialog';
import { BusinessPartnerSubRoutesModalComponent } from 'app/modules/components/routing/sub-routes/sub-route-periodicity/business-partner-sub-routes/business-partner-sub-routes.component';
import { SmartGridComponent } from 'app/shared/components/smart-grid/components/smart-grid/smart-grid.component';
import { ColDef } from 'app/shared/components/smart-grid/core/interfaces/col-def.interface';
import { GridConfig } from 'app/shared/components/smart-grid/core/interfaces/grid-config.interface';
import { PageChangedEvent } from 'app/shared/components/smart-grid/core/interfaces/pagination-state.interface';
import { SortChangedEvent } from 'app/shared/components/smart-grid/core/interfaces/sort-state.interface';
import { BusinessPartnerBySubRoute } from '../../interfaces/busines-partner-by-sub-route.interface';
import { AssignedPartnersListService } from './assigned-partners-list.service';
import { AddressCellRendererComponent } from './cell-renderers/address-cell-renderer/address-cell-renderer.component';
import { ClientCellRendererComponent } from './cell-renderers/client-cell-renderer/client-cell-renderer.component';
import { ContactCellRendererComponent } from './cell-renderers/contact-cell-renderer/contact-cell-renderer.component';
import { DeleteCellRendererComponent } from './cell-renderers/delete-cell-renderer/delete-cell-renderer.component';
import { FrequencyCellRendererComponent } from './cell-renderers/frequency-cell-renderer/frequency-cell-renderer.component';
import { RepinCellRendererComponent } from './cell-renderers/repin-cell-renderer/repin-cell-renderer.component';
import { RepineoStatusCellRendererComponent } from './cell-renderers/repineo-status-cell-renderer/repineo-status-cell-renderer.component';

@Component({
  selector: 'app-assigned-partners-list',
  standalone: true,
  imports: [CommonModule, SmartGridComponent],
  templateUrl: './assigned-partners-list.component.html',
  styles: [
    `
      :host {
        display: block;
        height: 100%;
        overflow: scroll;
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AssignedPartnersListComponent {
  private dialog = inject(MatDialog);
  readonly listService = inject(AssignedPartnersListService);

  // Exponer signals del servicio
  readonly data = this.listService.data;
  readonly totalItems = this.listService.totalItems;
  readonly loading = this.listService.loading;
  readonly pagination = this.listService.pagination;

  // Grid Config
  readonly gridConfig: GridConfig<BusinessPartnerBySubRoute> = {
    pagination: {
      enabled: true,
      mode: 'api',
      pageSize: 50,
      pageSizeOptions: [25, 50, 100],
      showPaginator: true,
    },
    sorting: {
      enabled: true,
      mode: 'api',
    },
    styling: {
      cellPaddingY: '1px',
      fontSize: '12px',
      // NOT the standard list height. This grid is a panel inside another screen, so a
      // viewport-relative floor would push its own paginator, and the page around it, out of
      // view. Height stays as the panel had it.
      maxHeight: '100%',
    },
    responsive: {
      enabled: false,
    },
    columnVisibility: {
      enabled: false,
    },
  };

  // Column Definitions
  readonly colDefs = computed<ColDef<BusinessPartnerBySubRoute>[]>(() => [
    {
      colId: 'client',
      headerName: 'routing.SUB_ROUTE_PARTNERS.LIST.COLUMNS.CLIENT',
      cellRenderer: ClientCellRendererComponent,
      cellRendererParams: {
        onOpenSubRoutes: (bp: BusinessPartnerBySubRoute) => this.openSubRoutesModal(bp),
        getCompanyId: () => this.listService.subRouteData()?.company?._id?.toString() ?? null,
      },
      sortable: true,
      minWidth: 280,
      flex: 1,
    },
    {
      colId: 'frequency',
      headerName: 'routing.SUB_ROUTE_PARTNERS.LIST.COLUMNS.FREQUENCY',
      cellRenderer: FrequencyCellRendererComponent,
      cellRendererParams: {
        getSubRouteId: () => this.listService.subRouteId(),
        getSubRouteData: () => this.listService.subRouteData(),
        onRefresh: () => this.listService.reload$.next(),
      },
      sortable: false,
      minWidth: 180,
      flex: 1,
    },

    {
      colId: 'address',
      headerName: 'EXPORT_COLUMNS.ADDRESS',
      cellRenderer: AddressCellRendererComponent,
      sortable: false,
      minWidth: 250,
      flex: 1,
    },
    {
      colId: 'useGpsMode',
      headerName: 'routing.SUB_ROUTE_PARTNERS.LIST.COLUMNS.GPS_MODE',
      cellRenderer: GpsModeCellRendererComponent,
      cellRendererParams: {
        onRefresh: () => this.listService.reload$.next(),
      },
      sortable: false,
      minWidth: 130,
      flex: 0.6,
    },
    {
      colId: 'contact',
      headerName: 'routing.SUB_ROUTE_PARTNERS.LIST.COLUMNS.CONTACT',
      cellRenderer: ContactCellRendererComponent,
      sortable: false,
      minWidth: 220,
      flex: 1,
    },
    {
      colId: 'geoStatus',
      headerName: 'routing.SUB_ROUTE_PARTNERS.LIST.COLUMNS.GEO_STATUS',
      cellRenderer: RepineoStatusCellRendererComponent,
      sortable: false,
      minWidth: 160,
    },
    {
      colId: 'repin',
      headerName: 'routing.SUB_ROUTE_PARTNERS.LIST.COLUMNS.REPIN',
      cellRenderer: RepinCellRendererComponent,
      cellRendererParams: {
        onGeoUpdateSuccess: () => this.listService.reload$.next(),
      },
      sortable: false,
      minWidth: 140,
    },
    {
      colId: 'delete',
      headerName: '',
      cellRenderer: DeleteCellRendererComponent,
      cellRendererParams: {
        getSubRouteId: () => this.listService.subRouteId(),
        onDeleteSuccess: () => this.listService.reload$.next(),
      },
      sortable: false,
      minWidth: 72,
      maxWidth: 72,
      cellClass: '!overflow-visible',
      pinned: 'right',
    },
  ]);

  // Computed para pageIndex (SmartGrid usa 0-based)
  readonly pageIndex = computed(() => (this.pagination().currentPage ?? 1) - 1);

  // Event Handlers
  onPageChange(event: PageChangedEvent): void {
    this.listService.updatePagination$.next({
      currentPage: event.pageIndex + 1,
      limitPerPage: event.pageSize,
    });
  }

  onSortChange(event: SortChangedEvent<BusinessPartnerBySubRoute>): void {
    if (event.sortStates.length > 0) {
      const sort = event.sortStates[0];
      this.listService.updatePagination$.next({
        sort: sort.field as any,
        sortDirection: sort.direction === 'asc' ? 'ASC' : 'DESC',
      });
    }
  }

  // Open SubRoutes Modal
  openSubRoutesModal(businessPartner: BusinessPartnerBySubRoute): void {
    this.dialog.open(BusinessPartnerSubRoutesModalComponent, {
      data: {
        businessPartner: businessPartner,
        subRoutes: businessPartner.subRoutes,
      },
    });
  }
}
